/**
 * Trimble Identity requires Serial PKCE on every /oauth/token call (login and refresh):
 * each request sends the previous code_verifier plus a new code_challenge, then stores
 * the next verifier for the following call. oidc-client-ts does not implement this
 * (see https://github.com/authts/oidc-client-ts/issues/1755), so we handle token
 * exchange and refresh here while react-oidc-context keeps login redirect and session.
 *
 * @see https://developer.trimble.com/docs/authentication/authorization-code-pkce/
 */
import config from "@/config";
import { isPlainObject } from "lodash-es";
import { User, type UserProfile } from "oidc-client-ts";
import * as z from "zod";

// In-memory Serial PKCE verifier for the next /token call (not sessionStorage).
let inMemoryTrimblePkceVerifier: string | undefined;

const PKCE_VERIFIER_BYTES = 64;
const PKCE_CHARSET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
const SERIAL_PKCE_METHOD = "S256";

const discoverySchema = z.object({
  token_endpoint: z.string(),
  userinfo_endpoint: z.string().optional(),
});

const tokenErrorSchema = z.object({
  error: z.string().optional(),
  error_description: z.string().optional(),
});

const tokenResponseSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string().optional(),
  id_token: z.string().optional(),
  expires_in: z.number().optional(),
  token_type: z.string().optional(),
  scope: z.string().optional(),
});

const idTokenPayloadSchema = z.looseObject({
  iss: z.string(),
  aud: z.union([z.string(), z.array(z.string())]),
  exp: z.number(),
  iat: z.number(),
  sub: z.string(),
});

const userInfoProfileSchema = z.looseObject({
  sub: z.string().optional(),
});

type TrimbleTokenResponse = z.infer<typeof tokenResponseSchema>;
type TrimbleDiscovery = z.infer<typeof discoverySchema>;

let cachedDiscovery: TrimbleDiscovery | undefined;
let refreshInFlight: Promise<User> | undefined;
let signInExchangeInFlight: Promise<User> | undefined;

function isPlainObjectType(value: unknown): value is Record<string, unknown> {
  return isPlainObject(value);
}

function getOidcRedirectUri(): string {
  return `${window.location.origin}/callback`;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "="
  );
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function decodeIdTokenProfile(idToken: string): UserProfile {
  const parts = idToken.split(".");
  if (parts.length !== 3) {
    throw new Error("Invalid id_token format");
  }

  const payloadPart = parts[1];
  if (payloadPart === undefined) {
    throw new Error("Invalid id_token payload");
  }

  const parsed: unknown = JSON.parse(decodeBase64Url(payloadPart));
  if (!isPlainObjectType(parsed)) {
    throw new Error("Invalid id_token payload");
  }

  return idTokenPayloadSchema.parse(parsed);
}

function mergeUserProfiles(
  base: UserProfile,
  ...sources: (Partial<UserProfile> | undefined)[]
): UserProfile {
  let merged: UserProfile = { ...base };
  for (const source of sources) {
    if (source !== undefined) {
      merged = { ...merged, ...source };
    }
  }
  return merged;
}

async function fetchTrimbleUserInfoProfile(
  accessToken: string,
  signal?: AbortSignal
): Promise<Partial<UserProfile> | undefined> {
  const discovery = await getTrimbleDiscovery(signal);
  const userInfoEndpoint = discovery.userinfo_endpoint;
  if (userInfoEndpoint === undefined) {
    return undefined;
  }

  const response = await fetch(userInfoEndpoint, {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal,
  });
  if (!response.ok) {
    return undefined;
  }

  const data: unknown = await response.json();
  if (!isPlainObjectType(data)) {
    return undefined;
  }

  return userInfoProfileSchema.parse(data);
}

async function enrichUserWithUserInfo(
  user: User,
  signal?: AbortSignal
): Promise<User> {
  // Login only — refresh keeps the stored profile; locale updates use useLocaleSync.
  const userInfoProfile = await fetchTrimbleUserInfoProfile(
    user.access_token,
    signal
  );
  if (userInfoProfile === undefined) {
    return user;
  }

  return new User({
    id_token: user.id_token,
    access_token: user.access_token,
    refresh_token: user.refresh_token,
    token_type: user.token_type,
    scope: user.scope,
    profile: mergeUserProfiles(user.profile, userInfoProfile),
    expires_at: user.expires_at,
    session_state: user.session_state,
  });
}

function generateCodeVerifier(): string {
  const randomValues = crypto.getRandomValues(
    new Uint8Array(PKCE_VERIFIER_BYTES)
  );
  let verifier = "";
  for (const value of randomValues) {
    verifier += PKCE_CHARSET.charAt(value % PKCE_CHARSET.length);
  }
  return verifier;
}

async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoded = new TextEncoder().encode(codeVerifier);
  const digest = await crypto.subtle.digest("SHA-256", encoded);
  return base64UrlEncode(new Uint8Array(digest));
}

async function generateTrimblePkcePair(): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  return { codeVerifier, codeChallenge };
}

export function getStoredTrimblePkceVerifier(): string | undefined {
  return inMemoryTrimblePkceVerifier;
}

export function setStoredTrimblePkceVerifier(codeVerifier: string): void {
  inMemoryTrimblePkceVerifier = codeVerifier;
}

export function clearStoredTrimblePkceVerifier(): void {
  inMemoryTrimblePkceVerifier = undefined;
}

export function isAuthCallbackPath(
  pathname = window.location.pathname
): boolean {
  return pathname === "/callback" || pathname.endsWith("/callback");
}

export function hasOidcCallbackParams(
  search = window.location.search
): boolean {
  const params = new URLSearchParams(search);
  return params.has("code") || params.has("error");
}

export function getTrimbleTokenRefreshDelayMs(
  expiresAt: number,
  bufferSeconds: number
): number {
  const refreshAtSeconds = expiresAt - bufferSeconds;
  const delayMs = (refreshAtSeconds - Math.floor(Date.now() / 1000)) * 1000;
  return Math.max(0, delayMs);
}

function readAuthorizationCode(url: string): string {
  const code = new URLSearchParams(new URL(url).search).get("code");
  if (code === null) {
    throw new Error("Missing authorization code in callback URL");
  }
  return code;
}

async function getTrimbleDiscovery(
  signal?: AbortSignal
): Promise<TrimbleDiscovery> {
  if (cachedDiscovery !== undefined) {
    return cachedDiscovery;
  }

  const response = await fetch(
    `${config.oidc.authority}/.well-known/openid-configuration`,
    { signal }
  );
  if (!response.ok) {
    throw new Error(`Discovery failed: ${response.status}`);
  }

  const data: unknown = await response.json();
  if (!isPlainObjectType(data)) {
    throw new Error("Invalid discovery document");
  }

  cachedDiscovery = discoverySchema.parse(data);
  return cachedDiscovery;
}

async function getTrimbleTokenEndpoint(signal?: AbortSignal): Promise<string> {
  const discovery = await getTrimbleDiscovery(signal);
  return discovery.token_endpoint;
}

function parseTokenError(data: unknown, status: number): Error {
  const parsed = tokenErrorSchema.safeParse(data);
  if (parsed.success) {
    const description =
      parsed.data.error_description ??
      parsed.data.error ??
      `Token request failed: ${status}`;
    return new Error(description);
  }
  return new Error(`Token request failed: ${status}`);
}

async function postTrimbleTokenRequest(
  body: Record<string, string>,
  signal?: AbortSignal
): Promise<TrimbleTokenResponse> {
  const tokenEndpoint = await getTrimbleTokenEndpoint(signal);
  const params = new URLSearchParams(body);

  const response = await fetch(tokenEndpoint, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: params,
    signal,
  });

  const data: unknown = await response.json();

  if (!response.ok) {
    throw parseTokenError(data, response.status);
  }

  return tokenResponseSchema.parse(data);
}

function buildExpiresAt(expiresIn: number | undefined): number | undefined {
  if (expiresIn === undefined) {
    return undefined;
  }
  return Math.floor(Date.now() / 1000) + expiresIn;
}

function createUserFromTokenResponse(
  tokenResponse: TrimbleTokenResponse,
  profile: UserProfile
): User {
  if (tokenResponse.id_token === undefined) {
    throw new Error("Token response is missing id_token");
  }

  return new User({
    id_token: tokenResponse.id_token,
    access_token: tokenResponse.access_token,
    refresh_token: tokenResponse.refresh_token,
    token_type: tokenResponse.token_type ?? "bearer",
    scope: tokenResponse.scope,
    profile,
    expires_at: buildExpiresAt(tokenResponse.expires_in),
    session_state: null,
  });
}

// Trimble chains PKCE: attach a new challenge to this request, persist the matching verifier for the next one.
async function exchangeWithSerialPkce(
  body: Record<string, string>,
  signal?: AbortSignal
): Promise<TrimbleTokenResponse> {
  const nextPkce = await generateTrimblePkcePair();
  const tokenResponse = await postTrimbleTokenRequest(
    {
      ...body,
      code_challenge: nextPkce.codeChallenge,
      code_challenge_method: SERIAL_PKCE_METHOD,
    },
    signal
  );
  setStoredTrimblePkceVerifier(nextPkce.codeVerifier);
  return tokenResponse;
}

export async function exchangeTrimbleAuthorizationCode(
  url: string,
  codeVerifier: string,
  signal?: AbortSignal
): Promise<User> {
  if (signInExchangeInFlight) {
    return signInExchangeInFlight;
  }

  signInExchangeInFlight = (async () => {
    const code = readAuthorizationCode(url);

    const tokenResponse = await exchangeWithSerialPkce(
      {
        grant_type: "authorization_code",
        code,
        client_id: config.oidc.client_id,
        redirect_uri: getOidcRedirectUri(),
        code_verifier: codeVerifier,
      },
      signal
    );

    if (tokenResponse.id_token === undefined) {
      throw new Error("Token response is missing id_token");
    }

    const user = createUserFromTokenResponse(
      tokenResponse,
      decodeIdTokenProfile(tokenResponse.id_token)
    );
    return enrichUserWithUserInfo(user, signal);
  })();

  try {
    return await signInExchangeInFlight;
  } finally {
    signInExchangeInFlight = undefined;
  }
}

export async function refreshTrimbleAccessToken(
  user: User,
  signal?: AbortSignal
): Promise<User> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const refreshToken = user.refresh_token;
    if (refreshToken === undefined || refreshToken.length === 0) {
      throw new Error("No refresh_token available for Trimble token refresh");
    }

    const codeVerifier = getStoredTrimblePkceVerifier();
    if (codeVerifier === undefined) {
      throw new Error("No stored Trimble PKCE verifier for token refresh");
    }

    const tokenResponse = await exchangeWithSerialPkce(
      {
        grant_type: "refresh_token",
        refresh_token: refreshToken,
        client_id: config.oidc.client_id,
        code_verifier: codeVerifier,
      },
      signal
    );

    const idToken = tokenResponse.id_token ?? user.id_token;
    if (idToken === undefined) {
      throw new Error("Token response is missing id_token");
    }

    const idTokenProfile = decodeIdTokenProfile(idToken);
    return new User({
      id_token: idToken,
      access_token: tokenResponse.access_token,
      refresh_token: tokenResponse.refresh_token ?? refreshToken,
      token_type: tokenResponse.token_type ?? user.token_type,
      scope: tokenResponse.scope ?? user.scope,
      profile: mergeUserProfiles(user.profile, idTokenProfile),
      expires_at: buildExpiresAt(tokenResponse.expires_in) ?? user.expires_at,
      session_state: user.session_state,
    });
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = undefined;
  }
}

export function resetTrimbleTokenRefreshTestState(): void {
  cachedDiscovery = undefined;
  refreshInFlight = undefined;
  signInExchangeInFlight = undefined;
  inMemoryTrimblePkceVerifier = undefined;
}
